import type { FeatureFlag } from '@ps/web-core-enums';

import type { request as apiRequest } from '@swf/core/api/request';
import { DebugKey, Header, Vertical } from '@swf/core/enums';
import { RequestError } from '@swf/core/errors/RequestError';
import { VerticalStatusError } from '@swf/core/errors/VerticalStatusError';
import type { RootState } from '@swf/core/store';
import { dispatch, select } from '@swf/core/store';
import type { BoundRequest, BoundRequestOptions } from '@swf/core/types/Request';
import { canUseDOM, renderer } from '@swf/core/utils';
import { createDebug } from '@swf/core/utils/debug';
import { handleUnauthorisedRequest } from '@swf/core/utils/handleUnauthorisedRequest';

type SportsRequestProps = Omit<BoundRequestOptions, 'service'> & {
  bypassBffCache?: boolean;
  includeFeatures?: FeatureFlag[];
  path: `/${string}`;
};
const defaultHttpMethod = 'GET';

const buildSportsRequest =
  (request: BoundRequest | typeof apiRequest, rootState: RootState) =>
  async <T>(requestOptions: SportsRequestProps) => {
    const {
      bypassBffCache = renderer === 'server',
      headers,
      includeFeatures = [],
      path,
      method = defaultHttpMethod,
      ...restOptions
    } = requestOptions;
    const accountCountry = select.account.getCountry(rootState);
    const isEligibleForPromotionalContent =
      select.sports.isEligibleForPromotionalContent(rootState);
    const enabledFeatureFlags = includeFeatures.filter((flag) =>
      select.feature.check(rootState, flag)
    );
    const { headers: swHeaders, meta } = select.cms.getRequestOptions(rootState);

    const sportsHeaders = {
      ...swHeaders,
      // Disable caching for requests from vader
      ...(bypassBffCache && { 'Cache-Control': 'max-age=0' }),
      // Pass account country for api gateway authorizer (content filtering)
      ...(accountCountry && { [Header.IspAccountCountry]: accountCountry }),
      ...(enabledFeatureFlags.length && { [Header.IspFeatures]: enabledFeatureFlags.join() }),
      ...(isEligibleForPromotionalContent && {
        [Header.IspPromotionsPermitted]: 'true'
      }),
      ...headers
    };

    const service = `/sports/web${path}`;
    const requestId = sportsHeaders[Header.RequestId];
    const debug = createDebug(DebugKey.Request, requestId);
    const isEmbedded = select.device.isEmbedded(rootState);

    if (!canUseDOM && meta.systemState.sports === 'red') {
      debug('sports request %s was rejected because the sports status is red', service);
      throw new VerticalStatusError(Vertical.Sports, service, method);
    }

    return request<T>({
      ...restOptions,
      headers: sportsHeaders,
      http: true,
      meta,
      service,
      method
    }).catch((error: unknown) => {
      if (error instanceof RequestError) {
        if (error.body.code === 'MAINTENANCE') {
          dispatch.system.update({ body: { sports: 'red' } });
        }

        if (canUseDOM && error.response?.status === 401) {
          handleUnauthorisedRequest(dispatch, isEmbedded, '/sports/logged-out');
        }
      }

      throw error;
    });
  };

export default buildSportsRequest;
